import { useCallback, useEffect, useMemo, useState } from 'react';

import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { Box, FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@material-ui/core';

import { toError, useGithubOrgs, validateGithubOrg } from '../../utils/github';
import { PickerSelect } from '../PickerSelect';

type ApiError = Error & {
  status?: number;
  statusCode?: number;
  response?: Response;
};

function getErrorType(
  err: ApiError,
): 'network' | 'server' | 'timeout' | 'auth' | 'notFound' | 'unknown' {
  const status = err.status || err.statusCode || err.response?.status;

  if (status === 404) {
    return 'notFound';
  }
  if (status === 401 || status === 403) {
    return 'auth';
  }
  if (status && status >= 500 && status < 600) {
    return 'server';
  }

  const errorMessage = err.message?.toLowerCase() || '';
  const isNetworkError =
    errorMessage.includes('network') ||
    errorMessage.includes('fetch') ||
    errorMessage.includes('failed to fetch') ||
    errorMessage.includes('econnrefused') ||
    err.name === 'NetworkError';

  const isNotFound =
    errorMessage.includes('not found') ||
    errorMessage.includes('404') ||
    (errorMessage.includes('host') && errorMessage.includes('not found'));

  const isTimeout = errorMessage.includes('timeout');

  if (isNetworkError) {
    return 'network';
  }
  if (isTimeout) {
    return 'timeout';
  }
  if (isNotFound) {
    return 'notFound';
  }
  return 'unknown';
}

export const GitHubProjectPickerField = (
  props: FieldExtensionComponentProps<string>,
) => {
  const {
    onChange,
    schema: { title = 'GitHub Organization', description = '' } = {},
    required,
    rawErrors,
    formContext,
    name,
    uiSchema,
  } = props;

  const configApi = useApi(configApiRef);
  const catalogApi = useApi(catalogApiRef);

  const selectedGithubOrg = (formContext?.formData?.[name] as string) || '';

  const featureFlagEnabled =
    configApi.getOptionalBoolean('configFeatureFlags.useGithubOrgsApi') ??
    false;

  const hostConfig = uiSchema?.['ui:options']?.host as string | undefined;
  const hostsConfig = uiSchema?.['ui:options']?.hosts as string[] | undefined;
  const orgNameFilter = uiSchema?.['ui:options']?.orgNameFilter as
    | string
    | undefined;

  const hasMultipleHosts =
    Array.isArray(hostsConfig) && hostsConfig.length > 1;

  const availableHosts = useMemo(() => {
    if (hasMultipleHosts) {
      return hostsConfig;
    }
    if (hostsConfig?.length === 1) {
      return hostsConfig;
    }
    if (hostConfig) {
      return [hostConfig];
    }
    return [];
  }, [hasMultipleHosts, hostsConfig, hostConfig]);

  const [selectedHost, setSelectedHost] = useState<string | undefined>(() => {
    if (hasMultipleHosts) {
      return availableHosts[0];
    }
    if (hostsConfig?.length === 1) {
      return hostsConfig[0];
    }
    return hostConfig;
  });

  const host =
    selectedHost ||
    hostConfig ||
    (availableHosts.length > 0 ? availableHosts[0] : undefined);

  const useGithubOrgsApi = featureFlagEnabled && !!host;

  const [useCatalogFallback, setUseCatalogFallback] = useState(false);
  const [fallbackWarning, setFallbackWarning] = useState<string | undefined>(
    undefined,
  );
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const {
    orgs: apiOrgs,
    loading: apiLoading,
    error: apiError,
  } = useGithubOrgs(useGithubOrgsApi ? host : undefined, refreshTrigger);

  const [catalogOptions, setCatalogOptions] = useState<
    Array<{ value: string; label: string; description?: string }>
  >([]);
  const [catalogLoading, setCatalogLoading] = useState<boolean>(false);
  const [catalogError, setCatalogError] = useState<Error | undefined>(
    undefined,
  );

  useEffect(() => {
    if (useGithubOrgsApi && !useCatalogFallback) {
      return () => {};
    }

    let isMounted = true;
    const fetchCatalogOrgs = async () => {
      setCatalogLoading(true);
      setCatalogError(undefined);
      try {
        const { items } = await catalogApi.getEntities({
          filter: {
            kind: 'Resource',
            'spec.type': 'github-organization',
          },
        });

        if (isMounted) {
          setCatalogOptions(
            items.map((entity: Entity) => ({
              value: stringifyEntityRef(entity),
              label: entity.metadata.title || entity.metadata.name,
              description: entity.metadata.description,
            })),
          );
        }
      } catch (e: unknown) {
        if (isMounted) {
          setCatalogError(toError(e));
        }
      } finally {
        if (isMounted) {
          setCatalogLoading(false);
        }
      }
    };

    fetchCatalogOrgs();
    return () => {
      isMounted = false;
    };
  }, [catalogApi, useGithubOrgsApi, useCatalogFallback, refreshTrigger]);

  useEffect(() => {
    if (!useGithubOrgsApi) {
      return;
    }

    if (apiError) {
      const errorType = getErrorType(apiError as ApiError);

      if (
        errorType === 'network' ||
        errorType === 'server' ||
        errorType === 'timeout'
      ) {
        setUseCatalogFallback(true);
        setFallbackWarning(
          'GitHub API unavailable. Showing organizations from catalog. Some entries may be outdated.',
        );
      } else {
        setUseCatalogFallback(false);
        setFallbackWarning(undefined);
        setCatalogOptions([]);
      }
    } else {
      setUseCatalogFallback(false);
      setFallbackWarning(undefined);
      if (useGithubOrgsApi) {
        setCatalogOptions([]);
      }
    }
  }, [useGithubOrgsApi, apiError]);

  useEffect(() => {
    if (hasMultipleHosts && availableHosts.length > 0) {
      if (!selectedHost || !availableHosts.includes(selectedHost)) {
        setSelectedHost(availableHosts[0]);
      }
    } else if (hostsConfig?.length === 1) {
      setSelectedHost(hostsConfig[0]);
    } else if (!hasMultipleHosts && hostConfig) {
      setSelectedHost(hostConfig);
    }
  }, [
    hasMultipleHosts,
    availableHosts,
    selectedHost,
    hostConfig,
    hostsConfig,
  ]);

  useEffect(() => {
    if (hasMultipleHosts && selectedHost) {
      setUseCatalogFallback(false);
      setFallbackWarning(undefined);
      setCatalogOptions([]);
    }
  }, [selectedHost, hasMultipleHosts]);

  useEffect(() => {
    if (!apiLoading && isRefreshing) {
      setIsRefreshing(false);
    }
  }, [apiLoading, isRefreshing]);

  const effectiveUseCatalog = !useGithubOrgsApi || useCatalogFallback;
  const loading = effectiveUseCatalog ? catalogLoading : apiLoading;
  const error = effectiveUseCatalog ? catalogError : apiError;

  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const handleHostChange = useCallback(
    (newHost: string) => {
      setSelectedHost(newHost);
      onChange('');
      setUseCatalogFallback(false);
      setFallbackWarning(undefined);
      setCatalogOptions([]);
      setRefreshTrigger(prev => prev + 1);
    },
    [onChange],
  );

  const options = useMemo(() => {
    const hasNotFoundError =
      apiError && getErrorType(apiError as ApiError) === 'notFound';

    if (hasNotFoundError) {
      return [];
    }

    let baseOptions: Array<{
      value: string;
      label: string;
      description?: string;
    }>;

    if (effectiveUseCatalog) {
      baseOptions = catalogOptions;
    } else {
      baseOptions = apiOrgs.map(org => {
        const label =
          hasMultipleHosts && org.metadata?.host
            ? `${org.metadata.host} - ${org.label}`
            : org.label;
        return {
          value: org.value,
          label,
          description: org.description,
        };
      });
    }

    if (orgNameFilter) {
      try {
        const regex = new RegExp(orgNameFilter);
        return baseOptions.filter(opt => regex.test(opt.label));
      } catch {
        return baseOptions;
      }
    }

    return baseOptions;
  }, [
    effectiveUseCatalog,
    apiOrgs,
    catalogOptions,
    orgNameFilter,
    hasMultipleHosts,
    apiError,
  ]);

  const isValidSelection =
    !selectedGithubOrg ||
    (effectiveUseCatalog
      ? options.some(opt => opt.value === selectedGithubOrg)
      : validateGithubOrg(selectedGithubOrg, apiOrgs));

  const errorText = useMemo(() => {
    if (rawErrors?.[0]) {
      return rawErrors[0];
    }

    if (error) {
      const errorType = getErrorType(error as ApiError);
      if (errorType === 'notFound') {
        return `GitHub host "${selectedHost || host}" not found or has no organizations. Please select a valid host.`;
      }
      if (fallbackWarning) {
        return fallbackWarning;
      }
      return error.message;
    }

    if (fallbackWarning) {
      return fallbackWarning;
    }

    if (!isValidSelection && selectedGithubOrg) {
      return 'Selected organization is not valid';
    }

    return undefined;
  }, [
    rawErrors,
    error,
    fallbackWarning,
    selectedHost,
    host,
    isValidSelection,
    selectedGithubOrg,
  ]);

  const loadingMessage = effectiveUseCatalog
    ? 'Loading organizations from catalog...'
    : 'Fetching organizations from GitHub...';

  const organizationDescription =
    description ||
    'Select the GitHub organization where the new repository will be created. This is not the repository name.';

  return (
    <Box>
      {hasMultipleHosts && (
        <Box marginBottom={2}>
          <FormControl fullWidth required={required}>
            <InputLabel id="github-host-selector-label">GitHub Host</InputLabel>
            <Select
              labelId="github-host-selector-label"
              id="github-host-selector"
              value={selectedHost || ''}
              onChange={e => handleHostChange(e.target.value as string)}
              label="GitHub Host"
            >
              {availableHosts.map(h => (
                <MenuItem key={h} value={h}>
                  {h}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Select the GitHub Enterprise host used to load organization options.
            </FormHelperText>
          </FormControl>
        </Box>
      )}

      <PickerSelect
        title={title}
        description={organizationDescription}
        required={required}
        value={selectedGithubOrg}
        onChange={onChange}
        loading={loading}
        loadingMessage={loadingMessage}
        errorText={errorText}
        options={options}
        labelId="github-org-picker-label"
        onRefresh={effectiveUseCatalog ? undefined : handleRefresh}
        refreshing={isRefreshing}
        enableSearch={!effectiveUseCatalog}
      />
    </Box>
  );
};
